import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { timeRangeFilterId, virtualMachineFilterId } from '../../filters';

const title = t`Virtual Machine CPU Ready Time`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['vmCpuReadyTime'],
    },
};

export function useChartConfig() {
    const color = useMetricColor('serverCpuReady');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`CPU Ready (ms)`,
                    },
                },
            ],
            chartType: 'spline',
            tooltip: BuiltInTooltip,
            showGranularity: true,
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.INTEGER,
                    units: ' ms',
                    chartType: 'spline',
                    key: 'vmCpuReadyTime',
                    label: t`CPU Ready`,
                    color,
                    multiplier: 1,
                },
            ],
        }),
        [color]
    );
}

function VirtualMachineCpuReadyPanel() {
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig();
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

VirtualMachineCpuReadyPanel.propTypes = panelPropTypes;

VirtualMachineCpuReadyPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareVmCpuReadyTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.VIRTUAL_MACHINE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`CPU ready time trend for a selected virtual machine (VM)`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, virtualMachineFilterId],
        optional: [],
    },
})(VirtualMachineCpuReadyPanel);
